import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import { resetAnalytics } from './analytics';
import { api, onUnauthorized } from './api';
import type { Billing, Me } from './types';

/* Who is signed in, and the team's billing summary the sidebar chip and the
   billing pages share. A 401 anywhere sends the console to /sign-in. */

interface SessionValue {
  me: Me | null;
  /** True until /v1/me has answered once. */
  checking: boolean;
  refreshMe: () => Promise<Me | null>;
  setMe: (me: Me | null) => void;
  /** Point the session at another of the user's teams (POST /v1/me/team). */
  switchTeam: (teamId: string) => Promise<Me | null>;
  signOut: () => Promise<void>;
}

const SessionContext = createContext<SessionValue | null>(null);

const SIGNED_OUT_PATHS = ['/sign-in', '/sign-up', '/sso-callback', '/verify', '/forgot-password', '/reset-password'];

export function SessionProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [checking, setChecking] = useState(true);
  const navigate = useNavigate();

  const refreshMe = useCallback(async () => {
    try {
      const answer = await api<Me>('/v1/me');
      setMe(answer);
      return answer;
    } catch {
      setMe(null);
      return null;
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    void refreshMe();
  }, [refreshMe]);

  useEffect(
    () =>
      onUnauthorized(() => {
        setMe(null);
        const here = window.location.pathname + window.location.search;
        /* The signed-out pages and the emailed links work signed out: a 401 from /v1/me there is expected. */
        if (!SIGNED_OUT_PATHS.some((path) => here.startsWith(path))) {
          navigate(`/sign-in?next=${encodeURIComponent(here)}`, { replace: true });
        }
      }),
    [navigate],
  );

  const switchTeam = useCallback(
    async (teamId: string) => {
      await api('/v1/me/team', { method: 'POST', body: { teamId } });
      return refreshMe();
    },
    [refreshMe],
  );

  const signOut = useCallback(async () => {
    try {
      await api('/v1/auth/logout', { method: 'POST' });
    } finally {
      resetAnalytics();
      setMe(null);
      navigate('/sign-in', { replace: true });
    }
  }, [navigate]);

  const value = useMemo(() => ({ me, checking, refreshMe, setMe, switchTeam, signOut }), [me, checking, refreshMe, switchTeam, signOut]);
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error('useSession outside SessionProvider');
  return value;
}

interface BillingValue {
  billing: Billing | undefined;
  reloadBilling: () => Promise<void>;
  setBilling: (b: Billing) => void;
}

const BillingContext = createContext<BillingValue | null>(null);

export function BillingProvider({ children }: { children: ReactNode }) {
  const [billing, setBilling] = useState<Billing | undefined>(undefined);
  const reloadBilling = useCallback(async () => {
    try {
      setBilling(await api<Billing>('/v1/billing'));
    } catch {
      /* the chip simply stays as it was */
    }
  }, []);
  useEffect(() => {
    void reloadBilling();
    const id = window.setInterval(() => {
      if (document.visibilityState === 'visible') void reloadBilling();
    }, 30_000);
    return () => window.clearInterval(id);
  }, [reloadBilling]);
  const value = useMemo(() => ({ billing, reloadBilling, setBilling }), [billing, reloadBilling]);
  return <BillingContext.Provider value={value}>{children}</BillingContext.Provider>;
}

export function useBilling(): BillingValue {
  const value = useContext(BillingContext);
  if (!value) throw new Error('useBilling outside BillingProvider');
  return value;
}
